/**
 * ExportMenu — export/handoff actions for Beat 3 (Share & Act).
 *
 * UX Spec Section 5.2.14 / Story 1.6 AC#6.
 */

import {
  Download,
  FileText,
  Presentation,
  RefreshCw,
  Shield,
  Send,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ExportMenuProps {
  score: number;
  onExportReport?: () => void;
  onExportPowerPoint?: () => void;
  onSaveRules?: () => void;
  onHandoff?: () => void;
  onExportJSON?: () => void;
  onReProfile?: () => void;
  onHandoffGuardian?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ExportMenu({
  onExportReport,
  onExportPowerPoint,
  onSaveRules,
  onHandoff,
  onExportJSON,
  onReProfile,
  onHandoffGuardian,
}: ExportMenuProps) {
  const actions = [
    {
      label: "Export PDF",
      icon: FileText,
      handler: onExportReport,
      minScore: 50,
    },
    {
      label: "Export PowerPoint",
      icon: Presentation,
      handler: onExportPowerPoint,
      minScore: 50,
    },
    { label: "Save Rules", icon: Download, handler: onSaveRules, minScore: 70 },
    { label: "Hand off", icon: Send, handler: onHandoff, minScore: 70 },
    {
      label: "Export JSON",
      icon: Download,
      handler: onExportJSON,
      minScore: 85,
    },
    { label: "Re-profile", icon: RefreshCw, handler: onReProfile, minScore: 0 },
    {
      label: "Hand off to Guardian",
      icon: Shield,
      handler: onHandoffGuardian,
      minScore: 0,
    },
  ];

  return (
    <nav
      role="navigation"
      aria-label="Next actions based on your data quality score"
      className="space-y-4"
    >
      {/* Action buttons — enabled purely by whether a handler is wired; the
          score is informational (guidance above), NOT a gate. Users may export
          / hand off at any score. */}
      <div className="flex flex-wrap gap-2">
        {actions.map(({ label, icon: Icon, handler }) => {
          const enabled = !!handler;
          return (
            <button
              key={label}
              onClick={enabled ? handler : undefined}
              disabled={!enabled}
              aria-disabled={!enabled}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg text-caption font-medium transition-colors
                ${
                  enabled
                    ? "glass-surface border border-[var(--border)] text-[var(--foreground)] hover:bg-white/5 cursor-pointer"
                    : "opacity-40 cursor-not-allowed glass-surface border border-[var(--border)]"
                }
              `}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
